/*
 * Copyright (C) 2017 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import {minBy, maxBy, flatMap} from 'es-toolkit/compat'
import {sortedUniqBy} from '@canvas/util/sortedUniqBy'

export class AnimatableRegistry {
  constructor() {
    this.registries = {
      day: {},
      group: {},
      item: {},
      opportunity: {},
      'new-activity-indicator': {},
    }
  }

  validateType(type) {
    const registryTypes = Object.keys(this.registries)
    if (!registryTypes.find(t => t === type)) {
      throw new Error(`invalid registry type ${type}`)
    }
  }

  register(type, component, index, componentIds) {
    this.validateType(type)
    const registry = this.registries[type]
    componentIds.forEach(componentId => (registry[componentId] = {component, index, componentIds}))
  }

  deregister(type, component, componentIds) {
    this.validateType(type)
    const registry = this.registries[type]
    componentIds.forEach(componentId => {
      if (registry[componentId].component === component) {
        delete registry[componentId]
      }
    })
  }

  getComponent(type, componentId) {
    this.validateType(type)
    return this.registries[type][componentId]
  }

  getFirstComponent(type, componentIds) {
    this.validateType(type)
    const registry = this.registries[type]
    const minItemId = minBy(componentIds, componentId => registry[componentId].index)
    return registry[minItemId]
  }

  getLastComponent(type, componentIds) {
    this.validateType(type)
    const registry = this.registries[type]
    const maxItemId = maxBy(componentIds, componentId => registry[componentId].index)
    return registry[maxItemId]
  }

  getUniqSortedComponents(type, componentIds) {
    this.validateType(type)
    const components = componentIds.map(componentId => this.registries[type][componentId])
    return sortedUniqBy(components, 'index')
  }

  // Gets all non-negative indexed components from the given registry in indexed order. Negative
  // indexed components are special and are not returned by this method. This method only makes
  // sense for days and opportunities since groups and items are nested components and will have
  // duplicate indexes registered (with different ids).
  getSortedComponents(type) {
    this.validateType(type)
    return sortedUniqBy(Object.values(this.registries[type]), 'index').filter(
      entryValue => entryValue.index >= 0,
    )
  }

  getAllGroupsSorted() {
    // get list of days sorted as they appear in the interface.
    const sortedDays = this.getSortedComponents('day')
    // get sorted groups for each sorted day, then flatten into one list of interface sorted groups.
    const sortedGroups = flatMap(sortedDays, day =>
      this.getUniqSortedComponents('group', day.componentIds),
    )
    return sortedGroups
  }

  // gets all items that are displayed in the interface in interface sorted order.
  getAllItemsSorted() {
    const sortedGroups = this.getAllGroupsSorted()
    // get sorted items for each group, then flatten into one list of interface sorted items
    const sortedItems = flatMap(sortedGroups, group =>
      this.getUniqSortedComponents('item', group.componentIds),
    )
    return sortedItems
  }

  // gets indexed opportunities in interface order.
  getAllOpportunitiesSorted() {
    return this.getSortedComponents('opportunity')
  }

  getAllNewActivityIndicatorsSorted() {
    const sortedGroups = this.getAllGroupsSorted()
    const mapped = flatMap(sortedGroups, group =>
      this.getUniqSortedComponents('new-activity-indicator', group.componentIds),
    )
    // not every group has a new activity indicator, so remove the undefined components
    const sortedNewActivityIndicators = mapped.filter(nai => nai != null)
    return sortedNewActivityIndicators
  }
}
